import { Campaign } from '@aslanpay/portal-common-lib';

/**
 * The zone every campaign's schedule is read in. One value for now rather than a choice: every company on
 * the platform runs to UK time, and a campaign that says otherwise would be a setting nobody meant to make.
 */
export const CAMPAIGN_TIMEZONE = 'Europe/London';

export type ScheduleMoment =
  | 'nominationsOpenAt'
  | 'nominationsCloseAt'
  | 'reviewOpensAt'
  | 'reviewClosesAt'
  | 'announcementAt';

/** Every moment of the schedule, in the order a cycle passes through them. */
export const SCHEDULE_MOMENTS: {
  key: ScheduleMoment;
  label: string;
  testId: string;
  help: string;
  optional?: boolean;
}[] = [
  {
    key: 'nominationsOpenAt',
    label: 'Nominations open',
    testId: 'nominations-open-input',
    help: 'People can start putting colleagues forward from this moment.',
  },
  {
    key: 'nominationsCloseAt',
    label: 'Nominations close',
    testId: 'nominations-close-input',
    help: 'Nominating stops. Nobody can be put forward after this.',
  },
  {
    key: 'reviewOpensAt',
    label: 'Review opens',
    testId: 'review-opens-input',
    help: 'The people running the campaign can start choosing a winner from the nominations.',
  },
  {
    key: 'reviewClosesAt',
    label: 'Review closes',
    testId: 'review-closes-input',
    help: 'Optional. A soft deadline that starts reminding the campaign managers to decide. It never expires the decision.',
    optional: true,
  },
  {
    key: 'announcementAt',
    label: 'Winner announced',
    testId: 'announcement-input',
    help: 'The winner is announced and their points are paid out.',
  },
];

/** The statuses a cycle holds once it has passed each moment. Mirrors the service's own rule. */
const REACHED_FROM: Record<ScheduleMoment, Campaign['status'][]> = {
  nominationsOpenAt: [
    'VOTING_OPEN',
    'VOTING_CLOSED',
    'INTERNAL_REVIEW_OPEN',
    'INTERNAL_REVIEW_CLOSED',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
  ],
  nominationsCloseAt: [
    'VOTING_CLOSED',
    'INTERNAL_REVIEW_OPEN',
    'INTERNAL_REVIEW_CLOSED',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
  ],
  reviewOpensAt: [
    'INTERNAL_REVIEW_OPEN',
    'INTERNAL_REVIEW_CLOSED',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
  ],
  reviewClosesAt: ['INTERNAL_REVIEW_CLOSED', 'COMPLETED', 'FAILED', 'CANCELLED'],
  announcementAt: ['COMPLETED', 'FAILED', 'CANCELLED'],
};

/**
 * Whether a moment of the schedule can still move: it can until the cycle reaches it, and not after.
 * A moment already passed is settled history, so moving it would rewrite what already happened.
 */
export const isMomentAhead = (moment: ScheduleMoment, status: Campaign['status']) =>
  !REACHED_FROM[moment].includes(status);

/** A moment as the form holds it, before it is an instant. */
export type ScheduleValues = Record<ScheduleMoment, string>;

/** A half-filled moment carries a date or a time but not both, which does not parse and is not yet set. */
export const isMomentSet = (value: string) => !!value && !Number.isNaN(new Date(value).getTime());

/** A moment as an instant, or nothing where it is not set yet. */
const at = (value: string) => (isMomentSet(value) ? new Date(value).getTime() : null);

/** A moment the schedule does not carry yet cannot be out of order, so an absent one is left alone. */
const isBefore = (moment: number | null, notBefore: number | null) =>
  moment !== null && notBefore !== null && moment < notBefore;

const isNotAfter = (earlier: number | null, later: number | null) =>
  earlier !== null && later !== null && later <= earlier;

/**
 * What is wrong with the schedule, a moment at a time.
 * <p>
 * Each moment is judged against the one before it and answers for itself, so a reader is told which step to
 * change rather than that the schedule as a whole is out of order. A rule answers as soon as both of its
 * moments are set, so a schedule filled in from the top is corrected as it goes rather than at the end. The
 * same rules the service applies, in the same order and the same words, so a form never refuses what the
 * service would accept or the reverse.
 *
 * @param stepMinutes the grid the lifecycle wakes on, which every time has to land on
 * @param openingMustBeAhead false where the campaign has already opened, so its opening is history not a plan
 */
export function scheduleProblems(
  values: ScheduleValues,
  stepMinutes: number,
  openingMustBeAhead = true
): Partial<Record<ScheduleMoment, string>> {
  const problems: Partial<Record<ScheduleMoment, string>> = {};

  SCHEDULE_MOMENTS.forEach(({ key, optional }) => {
    const value = values[key];

    if (!isMomentSet(value)) {
      // An optional moment nobody has started is simply absent; one half-filled is unfinished.
      if (!optional || value) {
        problems[key] = 'Set a date and a time.';
      }
      return;
    }

    if (new Date(value).getMinutes() % stepMinutes !== 0) {
      problems[
        key
      ] = `Times run on ${stepMinutes}-minute marks, which is when the schedule is acted on.`;
    }
  });

  const open = at(values.nominationsOpenAt);
  const close = at(values.nominationsCloseAt);
  const reviewOpens = at(values.reviewOpensAt);
  const reviewCloses = at(values.reviewClosesAt);
  const announce = at(values.announcementAt);

  // A rule answers as soon as both of its moments are set rather than once the last field is filled, so a
  // schedule entered a step at a time is corrected a step at a time. A moment already carrying a problem
  // keeps it: being told a time is off the grid is more use than being told it is out of order.
  const report = (key: ScheduleMoment, wrong: boolean, message: string) => {
    if (wrong && !problems[key]) {
      problems[key] = message;
    }
  };

  report(
    'nominationsOpenAt',
    openingMustBeAhead && isNotAfter(Date.now(), open),
    'Nominations must open in the future.'
  );
  report(
    'nominationsCloseAt',
    isNotAfter(open, close),
    'Nominations cannot close before they open.'
  );
  report(
    'reviewOpensAt',
    isBefore(reviewOpens, close),
    'The review cannot open before nominations close.'
  );
  report(
    'reviewClosesAt',
    isBefore(reviewCloses, reviewOpens),
    'The review cannot close before it opens.'
  );
  report(
    'announcementAt',
    isBefore(announce, reviewCloses ?? reviewOpens),
    'The winner cannot be announced before the review closes.'
  );

  return problems;
}
